/**
 * Project Settings Layout Component
 * 2-column layout: Sidebar (240px) + Content area
 *
 * Desktop (> 1024px): Sidebar visible
 * Mobile (< 1024px): Dropdown navigation
 *
 * Features:
 * - Shared page header (#2078): Aelo › Projects › <project> › Settings; the
 *   «Projects» crumb is the way back
 * - Save bar at the bottom of the content area (SettingsSaveBar), never in
 *   the header: top-right belongs to "create new" across the app
 * - Responsive sidebar/dropdown
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageBody } from '@/components/layout/PageBody';
import { SectionedPageFrame } from '@/components/layout/SectionedPageFrame';
import { SettingsSaveBar } from '@/components/settings/SettingsSaveBar';
import { ProjectSettingsHeader } from './ProjectSettingsHeader';
import {
  ProjectSettingsSidebar,
  ProjectSettingsMobileNav,
  getSectionTitleKey,
  type ProjectSettingsSection,
} from './ProjectSettingsSidebar';

interface ProjectSettingsLayoutProps {
  projectName: string;
  children: (activeSection: ProjectSettingsSection) => React.ReactNode;
  defaultSection?: ProjectSettingsSection;
  /** Controlled active section (synced to ?section= by the route) */
  activeSection?: ProjectSettingsSection;
  onSectionChange?: (section: ProjectSettingsSection) => void;
  isSaving?: boolean;
  /**
   * Settings have loaded. Defaults to true so callers that have nothing to
   * hydrate (and the existing tests) keep their current behaviour.
   */
  isLoaded?: boolean;
  /** Form differs from what the server last returned. */
  isDirty?: boolean;
  onSave?: () => void;
  className?: string;
}

// Sections saved by the global "Save Settings" button. Routing, Delivery and
// Channels persist themselves (mutations / own Save button); Danger Zone has
// nothing to save — showing the global button there misleads users.
const GLOBAL_SAVE_SECTIONS: ReadonlySet<ProjectSettingsSection> = new Set([
  'overview',
  'quality',
]);

/**
 * Main Project Settings Layout with responsive sidebar/dropdown navigation
 */
export function ProjectSettingsLayout({
  projectName,
  children,
  defaultSection = 'overview',
  activeSection: controlledSection,
  onSectionChange,
  isSaving = false,
  isLoaded = true,
  isDirty = false,
  onSave,
  className,
}: ProjectSettingsLayoutProps) {
  const { t } = useTranslation(['projects', 'common']);
  const [internalSection, setInternalSection] =
    useState<ProjectSettingsSection>(defaultSection);
  const activeSection = controlledSection ?? internalSection;

  const handleSectionChange = useCallback(
    (section: ProjectSettingsSection) => {
      setInternalSection(section);
      onSectionChange?.(section);
    },
    [onSectionChange]
  );

  const showGlobalSave = !!onSave && GLOBAL_SAVE_SECTIONS.has(activeSection);

  const { key: titleKey, fallback: titleFallback } = getSectionTitleKey(activeSection);
  const sectionTitle = t(titleKey, titleFallback);

  return (
    <PageBody fill className={className}>
      <ProjectSettingsHeader projectName={projectName} />
      <SectionedPageFrame
        mobileNav={
          <ProjectSettingsMobileNav
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
          />
        }
        sidebar={
          <ProjectSettingsSidebar
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
          />
        }
        sectionTitle={sectionTitle}
        sectionKey={activeSection}
        sectionTitleTestId="project-settings-section-title"
        contentTestId="project-settings-content"
      >
        {children(activeSection)}

        {/* Bottom-left, like every other settings screen. It used to sit
            in the header, top right — where "create new" lives on the
            list screens — so it both competed with that meaning and
            changed position depending on which tab was open. */}
        {showGlobalSave && (
          <SettingsSaveBar
            onSave={onSave}
            isSaving={isSaving}
            isLoaded={isLoaded}
            isDirty={isDirty}
            label={t('projects.saveSettings', 'Save Settings')}
            buttonTestId="project-settings-save"
          />
        )}
      </SectionedPageFrame>
    </PageBody>
  );
}
